import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useState } from "react";
import { ModalSubmitButton } from "@/components/ui/modal-submit-button";
import { BranchForm } from "@/features/branches/_components/branch-form";
import { StepPage } from "@/features/onboarding/_components/step-page";
import { handleConvexError } from "@/lib/errors";
import type { CreateBranchInput } from "@/lib/schemas/branch";

export const Route = createFileRoute("/onboarding/create-branch")({
	component: CreateBranchPage,
	validateSearch: (search: Record<string, unknown>) => ({
		org: search.org as string | undefined,
	}),
	beforeLoad: ({ search }) => {
		if (!search.org) {
			throw redirect({ to: "/onboarding/create-organization", replace: true });
		}
	},
});

function CreateBranchPage() {
	const { org } = Route.useSearch();
	const navigate = useNavigate();
	const createBranch = useMutation(api.features.branches.web.create);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (data: CreateBranchInput) => {
		if (!org) return;
		setIsSubmitting(true);
		try {
			await createBranch({
				orgId: org as Id<"organizations">,
				name: data.name,
				address: data.address,
				taxId: data.taxId,
				staffCommissionRate: data.staffCommissionRate,
			});
			navigate({ to: "/onboarding/invite-team", search: { org } });
		} catch (error) {
			handleConvexError(error, "Failed to create branch");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSkip = () => {
		navigate({ to: "/onboarding/invite-team", search: { org } });
	};

	const footer = (
		<div className="flex w-full flex-col gap-2">
			<ModalSubmitButton
				label="Create branch"
				formId="branch-form"
				isSubmitting={isSubmitting}
			/>
			<button
				type="button"
				onClick={handleSkip}
				className="mx-auto flex w-fit items-center gap-2 text-center text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-900"
			>
				I'll do this later
			</button>
		</div>
	);

	return (
		<StepPage
			title="Create your first branch"
			description="Add a physical location for your store. You can add more branches later."
			footer={footer}
		>
			<BranchForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
		</StepPage>
	);
}
